import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, message, Modal, Radio, Select, Switch, theme } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import EditIcon from "../../../../ui/EditIcon";
import styles from "./IpWhitelisting.module.css";
import { getFacilityListApi } from "../../../../../services/facilities.services";
import {
  getIpWhitelistingConfigsApi,
  upsertIpWhitelistingConfigApi,
} from "../../../../../services/ipWhitelisting.services";

const normalizeConfigList = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (data?.data && typeof data.data === "object") return [data.data];
  if (typeof data === "object") return [data];
  return [];
};

const humanMode = (mode) => {
  const m = (mode || "").toLowerCase();
  if (m === "permissive") return "Permissive";
  if (m === "strict") return "Strict";
  if (m === "strict_blocked") return "Strict";
  return "Disabled";
};

const pillClassForMode = (mode) => {
  const m = (mode || "").toLowerCase();
  if (m === "permissive") return styles.pillPermissive;
  if (m === "strict" || m === "strict_blocked") return styles.pillStrict;
  return styles.pillDisabled;
};

const IpWhitelisting = () => {
  const { token } = theme.useToken();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const configuredFacilityIds = useMemo(() => {
    const set = new Set();
    (configs || []).forEach((c) => {
      const fid = c?.facility_id ?? c?.facility?.id ?? c?.facility;
      if (fid !== undefined && fid !== null) set.add(String(fid));
    });
    return set;
  }, [configs]);

  const availableFacilityOptions = useMemo(() => {
    return (facilityOptions || []).filter(
      (o) => !configuredFacilityIds.has(String(o?.value))
    );
  }, [facilityOptions, configuredFacilityIds]);

  const facilitySelectOptions = useMemo(() => {
    if (!editing) return availableFacilityOptions;
    const editingFacilityId =
      editing?.facility_id ?? editing?.facility?.id ?? editing?.facility;
    const editingFacilityName =
      editing?.facility_name ||
      editing?.facility?.name ||
      facilityOptions.find((f) => f.value === editingFacilityId)?.label ||
      String(editingFacilityId ?? "");

    const current = editingFacilityId
      ? [{ value: editingFacilityId, label: editingFacilityName }]
      : [];

    const rest = (facilityOptions || []).filter(
      (o) => String(o?.value) !== String(editingFacilityId)
    );
    return [...current, ...rest];
  }, [editing, availableFacilityOptions, facilityOptions]);

  const loadFacilities = async () => {
    try {
      const res = await getFacilityListApi();
      const list = Array.isArray(res?.data) ? res.data : res?.data?.results || [];
      setFacilityOptions(
        list.map((f) => ({
          value: f?.id,
          label: f?.name,
        }))
      );
    } catch (e) {
      message.error("Failed to load facilities");
    }
  };

  const loadConfigs = async () => {
    setConfigs([]);
    setLoading(true);
    try {
      // Fetch once (no facility_id required), then filter client-side.
      const res = await getIpWhitelistingConfigsApi();
      const all = normalizeConfigList(res?.data);

      const byFacility = new Map();
      all.forEach((c) => {
        const fid = c?.facility_id ?? c?.facility?.id ?? c?.facility;
        if (fid == null) return;
        const enabled = !!c?.enabled;
        const mode = (c?.mode || "").toLowerCase();

        // Backend returns default rows for all facilities (enabled=false, mode=strict).
        // UX requirement: show table ONLY for facilities the user configured.
        // Our UI always persists a disabled config as mode="disabled", so we treat:
        // - enabled === true  OR  mode === "disabled"  as "configured"
        const isConfigured = enabled || mode === "disabled";
        if (isConfigured) byFacility.set(fid, c);
      });
      const next = Array.from(byFacility.values());
      setConfigs(next);
      setInitialLoadDone(true);
      return next;
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          e?.response?.data?.detail ||
          "Failed to load IP configurations"
      );
      setInitialLoadDone(true);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFacilities();
    loadConfigs();
  }, []);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      facility_id: undefined,
      enabled: false,
      mode: undefined,
    });
    if (!facilityOptions || facilityOptions.length === 0) {
      loadFacilities();
    }
    setModalOpen(true);
  };

  const openEdit = (config) => {
    setEditing(config);
    const facilityId = config?.facility_id ?? config?.facility?.id ?? config?.facility;
    const enabled = !!config?.enabled;
    const mode = (config?.mode || "").toLowerCase() || "disabled";
    form.resetFields();
    form.setFieldsValue({
      facility_id: facilityId,
      enabled,
      mode: enabled ? mode : "disabled",
    });
    setModalOpen(true);
  };

  const onEnabledChange = (checked) => {
    if (!checked) {
      form.setFieldValue("mode", undefined);
    } else {
    }
  };

  const onFinish = async (values) => {
    try {
      setSaving(true);

      const normalizedEnabled = values.mode === "disabled" ? false : values.enabled;
      const payload = {
        facility_id: values.facility_id,
        enabled: normalizedEnabled,
        mode: normalizedEnabled ? values.mode : "disabled",
      };

      const res = await upsertIpWhitelistingConfigApi(payload);
      if (!res) throw new Error("No response from server");

      const latest = await loadConfigs();

      const existsAfterSave = (latest || []).some((c) => {
        const fid = c?.facility_id ?? c?.facility?.id ?? c?.facility;
        return String(fid) === String(payload.facility_id);
      });
      if (!existsAfterSave) {
      } else {
        message.success("IP configuration saved");
      }

      setModalOpen(false);
      setEditing(null);
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          e?.response?.data?.detail ||
          "Failed to save configuration"
      );
    } finally {
      setSaving(false);
    }
  };

  const onFinishFailed = () => {
    message.error("Please fill the required fields");
  };

  const resolvedConfigs = useMemo(() => {
    return configs.map((c) => {
      const facilityId = c?.facility_id ?? c?.facility?.id ?? c?.facility;
      const facilityName =
        c?.facility_name ||
        c?.facility?.name ||
        facilityOptions.find((f) => f.value === facilityId)?.label ||
        "-";
      return {
        ...c,
        _facilityId: facilityId,
        _facilityName: facilityName,
      };
    });
  }, [configs, facilityOptions]);

  const empty = !loading && (resolvedConfigs?.length || 0) === 0;
  const showDebug = process.env.NODE_ENV !== "production";
  const isInitialLoading = !initialLoadDone && loading;

  return (
    <div
      className={styles.container}
      style={{
        ["--org-primary"]: token.colorPrimary,
        ["--org-primary-bg"]: token.colorPrimaryBg,
        ["--org-primary-soft"]: token.colorPrimaryBg,
      }}
    >
      {isInitialLoading ? (
        <div className={styles.emptyCard} style={{ minHeight: 320 }}>
          <div className={styles.emptyInner}>
            <div className={styles.emptyTitle}>Loading configurations…</div>
          </div>
        </div>
      ) : empty ? (
        <div className={styles.emptyCard}>
          <div className={styles.emptyInner}>
            <div className={styles.emptyIcon}>
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 56 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 12C0 5.37258 5.37258 0 12 0H44C50.6274 0 56 5.37258 56 12V44C56 50.6274 50.6274 56 44 56H12C5.37258 56 0 50.6274 0 44V12Z"
                    fill="var(--org-primary-bg, rgba(127, 86, 217, 0.10))"
                  />
                  <path
                    d="M28.0002 16.3333L16.3335 22.1666L28.0002 27.9999L39.6668 22.1666L28.0002 16.3333Z"
                    stroke="var(--org-primary, #7F56D9)"
                    strokeWidth="2.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16.3335 33.8333L28.0002 39.6666L39.6668 33.8333"
                    stroke="var(--org-primary, #7F56D9)"
                    strokeWidth="2.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16.3335 28L28.0002 33.8333L39.6668 28"
                    stroke="var(--org-primary, #7F56D9)"
                    strokeWidth="2.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
            </div>
            <div className={styles.emptyTitle}>No IP configurations yet</div>
            <div className={styles.emptySubtitle}>
              Get started by adding your first IP whitelist configuration
              <br />
              to control access to your facilities.
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className={styles.primaryBtn}
              onClick={openAdd}
              style={{
                background: token.colorPrimary,
                borderColor: token.colorPrimary,
              }}
            >
              Add Configuration
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <div className={styles.cardTitle}>IP Configurations</div>
              <div className={styles.cardSubtitle}>
                Manage IP Configuration for facilities
              </div>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className={styles.primaryBtn}
              onClick={openAdd}
              style={{
                background: token.colorPrimary,
                borderColor: token.colorPrimary,
              }}
            >
              Add Configuration
            </Button>
          </div>

          <div className={styles.tableHeader}>
            <div>Facility</div>
            <div className={styles.headerCenter}>Status</div>
            <div className={styles.headerCenter}>Mode</div>
            <div style={{ textAlign: "right" }}>Action</div>
          </div>

          {resolvedConfigs.map((c) => {
            const enabled = !!c?.enabled;
            const mode = enabled ? c?.mode : "-";
            return (
              <div key={c?.id ?? c?._facilityId} className={styles.row}>
                <div className={styles.facilityName}>{c?._facilityName}</div>
                <div className={styles.centerCell}>
                  <span
                    className={`${styles.pill} ${
                      enabled ? styles.pillEnabled : styles.pillDisabled
                    }`}
                  >
                    <span className={styles.pillDot} />
                    {enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <div>
                  <div className={styles.centerCell}>
                    {enabled ? (
                      <span className={`${styles.pill} ${pillClassForMode(mode)}`}>
                        <span className={styles.pillDot} />
                        {humanMode(mode)}
                      </span>
                    ) : (
                      <span style={{ color: "#98A2B3", fontWeight: 600 }}>-</span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <button
                    className={styles.actionBtn}
                    onClick={() => openEdit(c)}
                    aria-label="Edit configuration"
                  >
                    <EditIcon />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        footer={null}
        closable
        centered
        width={620}
        title={<div className={styles.modalTitle}>Add IP Configuration</div>}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 6 }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          <Form.Item
            label={<span className={styles.fieldLabel}>Facility *</span>}
            name="facility_id"
            rules={[{ required: true, message: "Please select facility" }]}
          >
            <Select
              placeholder="Select Facility"
              options={facilitySelectOptions}
              disabled={!!editing}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <div
            className="flex-start"
            style={{ justifyContent: "space-between", gap: 12 }}
          >
            <div>
              <div className={styles.fieldLabel}>Enable IP Whitelisting</div>
              <div className={styles.hintText}>
                Allow access from this IP address
              </div>
            </div>
            <Form.Item name="enabled" valuePropName="checked" noStyle>
              <Switch onChange={onEnabledChange} />
            </Form.Item>
          </div>

          <div className={styles.divider} />

          <Form.Item shouldUpdate noStyle>
            {() => {
              const enabled = form.getFieldValue("enabled");
              if (!enabled) return null;
              return (
                <>
                  <div className={styles.policyTitle}>Access Policy</div>
                  <Form.Item
                    name="mode"
                    className={styles.radioBlock}
                    rules={[
                      { required: true, message: "Please select an access policy" },
                    ]}
                  >
                    <Radio.Group>
                      <Radio value="permissive">
                        <div>
                          <div className={styles.radioMain}>Permissive</div>
                          <div className={styles.radioSub}>
                            Allow access from any IP, log violations for
                            monitoring
                          </div>
                        </div>
                      </Radio>
                      <Radio value="strict">
                        <div>
                          <div className={styles.radioMain}>Strict</div>
                          <div className={styles.radioSub}>
                            Block access from any IP not on the whitelist
                          </div>
                        </div>
                      </Radio>
                      <Radio value="disabled">
                        <div>
                          <div className={styles.radioMain}>Disable</div>
                          <div className={styles.radioSub}>
                            Turn off IP whitelisting for this facility
                          </div>
                        </div>
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>

          <div className={styles.modalFooter}>
            <Button
              className={styles.secondaryBtn}
              onClick={() => {
                setModalOpen(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className={styles.primaryBtn}
              loading={saving}
              htmlType="submit"
              style={{
                background: token.colorPrimary,
                borderColor: token.colorPrimary,
              }}
            >
              Save Configuration
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default IpWhitelisting;

